// src/data/siteContent.js
import Logo from "../images/name_logo2.png";
import Hero from "../images/hero.png";
import Hero1 from "../images/hero1.png";

export const siteMeta = {
  brand: "Dryvziro",
  logo: Logo,
  hero: Hero,
  hero1: Hero1,
  tagline: "by Gokijany",
  ctaHref: "https://wa.me/254705247958?text=Hi%20Dryvziro%2C%20I%20want%20to%20start%20a%20free%20fuel%20pilot%20for%20my%20fleet",
  ctaLabel: "Free Fuel Audit",
  whatsappHref: "https://wa.me/254705247958?text=Hi%20Dryvziro%2C%20I%27d%20like%20to%20know%20more%20about%20the%20free%20fuel%20pilot",
  contactEmail: "hello@dryvziro.com",
  contactPhone: "+254 705 247 958",
  location: "Nairobi, Kenya",
  copyright: "© 2026 Dryvziro by Electrixitaty Limited. All rights reserved.",
};

export const hero = {
  eyebrow: "FLEET FUEL EFFICIENCY FOR NAIROBI OPERATORS",
  title: "We find where your fleet is wasting fuel, and show you how to stop it.",
  subtitle:
    "Most Nairobi fleets lose 15–25% of their fuel to vehicles burning more than they should. Dryvziro tracks every vehicle, flags the inefficiency, and tells you exactly what to fix, so your savings show up in the next billing cycle.",
  primaryCta: "Start Your Free Fuel Audit",
  secondaryCta: "See How It Works  →",
  stats: [
    { value: "10–20%", label: "Fuel reduction in 30 days" },
    { value: "KES 30K–80K", label: "Saved per fleet per month" },
    { value: "Week 1", label: "When results first appear" },
  ],
  audienceButtons: [
    { label: "Transport Operators  →", target: "#how" },
    { label: "Petrol Stations  →", target: "#partners" },
    { label: "Government  →", target: "#partners" },
    { label: "NGOs  →", target: "#partners" },
    { label: "Corporates  →", target: "#partners" },
  ],
};

export const problemSolution = {
  eyebrow: "WHY DRYVZIRO EXISTS",
  title: "You know fuel costs are rising. You don't always know which vehicle is burning more than it should. That's exactly what Dryvziro tells you.",
  intro: "Dryvziro closes the gap between fuel spend and operational visibility, turning fragmented transport data into a clear picture of where money is being lost and what to do about it.",
  problem: {
    eyebrow: "THE FLEET OPERATOR'S PROBLEM",
    title: "Hidden Fuel Waste Is Costing Your Fleet Every Month",
    desc: "Most Nairobi fleet operators don't know which of their vehicles is over-consuming until the fuel receipts add up at month end. By then, the waste has already happened. Dryvziro surfaces it in real time, per vehicle, every day.",
    indicator: "10–20%  ·  Average fuel waste detected per fleet",
  },
  solutions: [
    {
      title: "Fuel Tracking",
      desc: "We capture daily fuel intake per vehicle and calculate the exact consumption rate, flagging any vehicle that deviates from its own baseline.",
    },
    {
      title: "Anomaly Detection",
      desc: "When a vehicle starts burning more fuel than it should, Dryvziro alerts you before it becomes an expensive problem.",
    },
    {
      title: "Actionable Reports",
      desc: "You receive a clear weekly report: which vehicles to service, what was saved, and what your fleet's efficiency looks like over time.",
    },
  ],
};

export const howItWorks = {
  eyebrow: "YOUR JOURNEY",
  title: "How Dryvziro Works for Your Fleet",
  description:
    "No complex software. No lengthy onboarding. You share the fuel data. We tell you what it means.",
  steps: [
    {
      number: "01",
      title: "You Share Daily Fuel Logs",
      desc: "Each day, your drivers or fuelling station share basic data: vehicle, litres added, odometer reading. This takes under 2 minutes per vehicle and can be done via WhatsApp or a simple form. Nothing to install.",
    },
    {
      number: "02",
      title: "We Calculate Each Vehicle's Fuel Rate",
      desc: "Dryvziro computes the fuel consumption rate per vehicle and compares it against its own baseline. No assumptions. No averages across your fleet. Each vehicle is tracked individually.",
    },
    {
      number: "03",
      title: "We Flag the Inefficient Vehicles",
      desc: "When a vehicle starts consuming more than it should, you receive an alert: which vehicle, by how much, and for how long. You know exactly where to focus, before the cost compounds.",
    },
    {
      number: "04",
      title: "You Fix It and See the Savings",
      desc: "After the flagged vehicle is serviced, fuel consumption drops. Your next report shows the before and after, in litres and in KES. Your savings are documented, real, and verifiable.",
    },
  ],
  ctaLink: "See a sample fuel report  →",
};

export const impactData = {
  eyebrow: "PROJECTED IMPACT AT SCALE",
  title: "What Dryvziro Is Built to Achieve Across Nairobi's Transport Network",
  description:
    "These figures represent Dryvziro's projected impact when deployed across Nairobi's vehicle ecosystem. We are currently in pilot phase. Real results from active clients will be published here as they are verified.",
  headline: [
    { value: "13M", label: "TONNES CO₂ REDUCTION", sublabel: "(Projected target)" },
    { value: "2.6M", label: "VEHICLES COVERED", sublabel: "(Projected target)" },
    { value: "215K", label: "JOBS CREATED", sublabel: "(Projected target)" },
    { value: "1,850", label: "TREES EQUIVALENT IMPACT", sublabel: "(Projected target)" },
  ],
  supporting: [
    { value: "175", label: "Communities involved", sublabel: "(Projected target)" },
    { value: "1,455", label: "Project duration days" },
    { value: "3,200", label: "Trees planted", sublabel: "(Projected target)" },
  ],
};

export const portfolio = {
  eyebrow: "WHAT DRYVZIRO DELIVERS",
  title: "From Fleet Data to Measurable Outcomes.",
  description:
    "Dryvziro's data feeds into six measurable intervention areas, each backed by verified transport intelligence, not assumptions.",
  categories: ["All", "Efficiency", "Mobility", "Nature", "Community"],
  items: [
    {
      title: "Tree Planting",
      desc: "Geotagged and verified reforestation initiatives tied to measurable environmental contribution from transport participants.",
      tag: "Nature",
    },
    {
      title: "Vehicle Efficiency",
      desc: "Operational improvements that reduce waste, improve fleet behaviour, and cut fuel costs and emissions over time.",
      tag: "Efficiency",
    },
    {
      title: "Clean Transport",
      desc: "Support for low-emission transport transition pathways and cleaner mobility planning across Nairobi's urban network.",
      tag: "Mobility",
    },
    {
      title: "Community Projects",
      desc: "Urban environmental programmes focused on high-impact zones and public health relevance.",
      tag: "Community",
    },
    {
      title: "Institutional Reporting",
      desc: "Readable outputs for governments, NGOs, and partners who need more than generic climate narratives, verified data they can act on.",
      tag: "Efficiency",
    },
    {
      title: "Air Quality Visibility",
      desc: "PM2.5 and transport-linked pollution intelligence to help anchor targeted intervention planning and public health decisions.",
      tag: "Mobility",
    },
  ],
};

export const stakeholders = {
  eyebrow: "WHO IT SERVES",
  title: "Built for every operator, business, and institution that moves Nairobi.",
  featured: "Transport Operators",
  items: [
    "Transport Operators",
    "Corporates",
    "NGOs",
    "Government",
    "Petrol Stations",
    "Mobility Programs",
    "Climate Financiers",
    "Urban Planners",
  ],
};

export const technologies = {
  eyebrow: "TECHNOLOGY & TRANSPARENCY",
  title: "The system layer that makes Dryvziro credible.",
  items: [
    {
      title: "Your Data, Your Records",
      desc: "Every fuel log and report is permanently recorded and cannot be altered. Your data belongs to you, export it, share it with your accountant, or use it for compliance at any time.",
    },
    {
      title: "AI Monitoring",
      desc: "Our algorithms calculate each vehicle's fuel rate daily. When a vehicle deviates from its own baseline, you are alerted immediately, not at month end.",
    },
    {
      title: "Data Dashboard",
      desc: "A clean, readable report delivered weekly. See your fleet's fuel performance at a glance, drill into individual vehicles, and track your savings over time. No training required.",
    },
  ],
  closingCta: {
    headline: "Ready to cut your fleet's fuel bill?",
    body: "Start your free 30-day fuel audit. No commitment. No app required. WhatsApp us now and we'll set up your fleet in under 10 minutes.",
    button: "WhatsApp Us Now  →",
  },
};

export const freePilotSignup = {
  eyebrow: "START YOUR FREE PILOT",
  title: "Ready to see where your fleet is wasting fuel?",
  subtitle: "Your first 30 days are completely free. No app to install. No commitment required. Share your fuel data with us, we'll show you the waste.",
  features: [
    "10–20% average fuel reduction in 30 days",
    "Per-vehicle reporting, you see exactly which vehicle to fix",
    "Free pilot, no contract, results in week one",
  ],
  form: {
    name: {
      label: "Full Name",
      placeholder: "Your name",
    },
    phone: {
      label: "Phone Number",
      placeholder: "+254 7XX XXX XXX",
    },
    fleetSize: {
      label: "Fleet Size",
      options: ["5–10 vehicles", "11–20 vehicles", "21–50 vehicles", "50+ vehicles"],
    },
    message: {
      label: "Message (optional)",
      placeholder: "Tell us about your fleet, what vehicles, what routes, your biggest fuel challenge",
    },
    submitButton: "Send My Request",
    successMessage: "Opening WhatsApp, we'll be in touch within 24 hours.",
    note: "We respond within 24 hours. Setup takes under 10 minutes.",
  },
};
